import { useEffect, useState } from "react";

/**
 * Live countdown to a target ISO datetime. Renders "Nd Nh Nm Ns" while active
 * and gracefully switches to an "expired" state when the target passes.
 *
 * Props:
 *  - expiresAt:  ISO 8601 string
 *  - lang:       "en" | "it"
 *  - onExpire:   optional callback fired once when the countdown hits zero
 *  - expiredLabel: string to show when expired
 *  - liveLabel:    string to prefix the countdown (e.g. "Expires in")
 */
export default function BonusCountdown({ expiresAt, lang = "en", onExpire, expiredLabel, liveLabel }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  const target = expiresAt ? new Date(expiresAt).getTime() : 0;
  const diff = target - now;

  useEffect(() => {
    if (diff <= 0 && onExpire) onExpire();
    // Only fire when we cross zero
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diff <= 0]);

  if (!expiresAt) return null;

  if (diff <= 0) {
    return (
      <span data-testid="bonus-countdown-expired" className="font-mono">
        {expiredLabel || (lang === "it" ? "Scaduto" : "Expired")}
      </span>
    );
  }

  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, "0");

  const parts = [];
  if (days > 0) parts.push(`${days}${lang === "it" ? "g" : "d"}`);
  parts.push(`${pad(hours)}h`);
  parts.push(`${pad(minutes)}m`);
  parts.push(`${pad(seconds)}s`);

  return (
    <span data-testid="bonus-countdown" className="font-mono">
      {liveLabel ? `${liveLabel} ` : ""}
      {parts.join(" ")}
    </span>
  );
}
